import { DecimalPipe } from '@angular/common';
import { Component, OnInit, inject, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InsuranceAiCopilotChatResult, InsuranceAiRecommendationRecord, InsuranceApiService } from './insurance-api.service';

@Component({
  standalone: true,
  selector: 'ain-insurance-ai-copilot-panel',
  imports: [DecimalPipe, FormsModule, ButtonModule, TextareaModule, AinStateComponent, RouterLink],
  template: `
    <aside class="copilot" [class.collapsed]="collapsed()">
      <header>
        <strong>{{ t('insurance.ai.copilot.title') }}</strong>
        <button type="button" class="toggle" (click)="collapsed.set(!collapsed())">
          {{ collapsed() ? t('insurance.ai.copilot.expand') : t('insurance.ai.copilot.collapse') }}
        </button>
      </header>
      @if (!collapsed()) {
        <p class="hint">{{ t('insurance.ai.copilot.advisoryOnly') }}</p>
        @if (!entityId()) {
          <ain-state type="empty" [title]="t('insurance.ai.copilot.noEntity')" />
        } @else if (permissionDenied()) {
          <ain-state type="error" [title]="t('insurance.states.noPermission')" />
        } @else if (loading()) {
          <ain-state type="loading" [title]="t('common.loading')" />
        } @else {
          <div class="actions">
            <button pButton type="button" size="small" [label]="t('insurance.ai.copilot.refresh')" (click)="load()"></button>
            <button pButton type="button" size="small" severity="secondary" [label]="t('insurance.ai.copilot.ask')" [disabled]="chatting()" (click)="ask()"></button>
          </div>
          @for (row of recommendations(); track row.id) {
            <article class="rec">
              <div class="meta">
                <span class="type">{{ row.recommendationType }}</span>
                <span class="score">{{ (row.score * 100) | number:'1.0-0' }}%</span>
              </div>
              <p>{{ summaryFor(row) }}</p>
              <div class="rec-actions">
                <a [routerLink]="['/insurance/ai-recommendations', row.id]">{{ t('insurance.ai.copilot.viewDetail') }}</a>
                <button pButton type="button" size="small" [label]="t('insurance.ai.copilot.accept')" [disabled]="row.status !== 'Pending'" (click)="accept(row.id)"></button>
                <button pButton type="button" size="small" severity="secondary" [label]="t('insurance.ai.copilot.override')" [disabled]="row.status !== 'Pending'" (click)="openOverride(row.id)"></button>
              </div>
            </article>
          } @empty {
            <p class="empty">{{ t('insurance.ai.copilot.empty') }}</p>
          }
          @if (overrideTargetId()) {
            <section class="override-box">
              <label>{{ t('insurance.ai.copilot.overrideReason') }}</label>
              <textarea pTextarea rows="2" [(ngModel)]="overrideReason"></textarea>
              <div class="actions">
                <button pButton type="button" size="small" [label]="t('common.confirm')" [disabled]="!overrideReason.trim()" (click)="confirmOverride()"></button>
                <button pButton type="button" size="small" severity="secondary" [label]="t('common.cancel')" (click)="cancelOverride()"></button>
              </div>
            </section>
          }
          <section class="note-box">
            <label>{{ t('insurance.ai.copilot.questionLabel') }}</label>
            <textarea pTextarea rows="3" [(ngModel)]="questionText"></textarea>
          </section>
          @if (lastAnswer()) {
            <p class="answer">{{ lastAnswer() }}</p>
          }
          @if (lastChatResult() && !lastChatResult()!.isGrounded) {
            <p class="warning">{{ t('insurance.ai.copilot.insufficientContext') }}</p>
          }
          @if (lastCitations().length > 0) {
            <section class="citations">
              <strong>{{ t('insurance.ai.copilot.citations') }}</strong>
              <ul>
                @for (citation of lastCitations(); track citation.documentId + ':' + citation.chunkIndex) {
                  <li>{{ citation.documentTitle }} ({{ t('insurance.ai.copilot.chunk') }} {{ citation.chunkIndex }})</li>
                }
              </ul>
            </section>
          }
        }
      }
    </aside>
  `,
  styles: [`
    .copilot { border: 1px solid #dbeafe; border-radius: 12px; padding: 12px; background: #f8fafc; min-width: 260px; margin-top: 12px; }
    .copilot.collapsed { min-width: auto; }
    header { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-bottom: 8px; }
    .toggle { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-size: 12px; }
    .hint { margin: 0 0 8px; font-size: 12px; color: #6b7280; }
    .actions { display: flex; gap: 6px; margin-bottom: 8px; flex-wrap: wrap; }
    .rec { border: 1px solid #e5e7eb; border-radius: 8px; padding: 8px; margin-bottom: 8px; background: #fff; }
    .meta { display: flex; justify-content: space-between; font-size: 12px; color: #374151; margin-bottom: 4px; }
    .type { font-weight: 600; }
    .score { color: #1d4ed8; }
    .rec p { margin: 0 0 6px; font-size: 13px; }
    .rec-actions { display: flex; gap: 6px; flex-wrap: wrap; align-items: center; }
    .rec-actions a { color: #1d4ed8; font-size: 12px; font-weight: 600; text-decoration: none; }
    .empty, .answer { font-size: 13px; color: #6b7280; }
    .warning { font-size: 12px; color: #b45309; margin: 6px 0 0; }
    .citations { margin-top: 8px; font-size: 12px; color: #374151; }
    .citations ul { margin: 4px 0 0; padding-inline-start: 18px; }
    .note-box, .override-box { display: grid; gap: 6px; margin-top: 10px; }
    label { font-size: 12px; color: #6b7280; }
  `]
})
export class InsuranceAiCopilotPanelComponent implements OnInit {
  private readonly api = inject(InsuranceApiService);
  private readonly localization = inject(LocalizationService);

  readonly entityType = input.required<string>();
  readonly entityId = input<string | null | undefined>(null);

  readonly loading = signal(false);
  readonly chatting = signal(false);
  readonly collapsed = signal(false);
  readonly permissionDenied = signal(false);
  readonly recommendations = signal<InsuranceAiRecommendationRecord[]>([]);
  readonly overrideTargetId = signal<string | null>(null);
  readonly lastAnswer = signal('');
  readonly lastChatResult = signal<InsuranceAiCopilotChatResult | null>(null);
  readonly lastCitations = signal<InsuranceAiCopilotChatResult['citations']>([]);
  overrideReason = '';
  questionText = '';

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    const id = this.entityId();
    if (!id) return;
    this.loading.set(true);
    this.permissionDenied.set(false);
    this.api.listAiRecommendations({ entityType: this.entityType(), entityId: id }).subscribe({
      next: rows => {
        this.recommendations.set(rows.slice(0, 5));
        this.loading.set(false);
      },
      error: err => {
        this.recommendations.set([]);
        this.loading.set(false);
        if (err?.status === 403) this.permissionDenied.set(true);
      }
    });
  }

  ask(): void {
    const id = this.entityId();
    if (!id) return;
    this.chatting.set(true);
    this.api.copilotChat({
      entityType: this.entityType(),
      entityId: id,
      message: this.questionText.trim() || undefined,
      contextJson: JSON.stringify({ entityType: this.entityType(), entityId: id, source: 'copilot-panel' })
    }).subscribe({
      next: payload => {
        this.lastChatResult.set(payload);
        this.lastAnswer.set(payload.answer);
        this.lastCitations.set(payload.citations ?? []);
        this.chatting.set(false);
        this.load();
      },
      error: () => this.chatting.set(false)
    });
  }

  accept(id: string): void {
    this.api.acceptAiRecommendation(id).subscribe({ next: () => this.load() });
  }

  openOverride(id: string): void {
    this.overrideTargetId.set(id);
    this.overrideReason = '';
  }

  confirmOverride(): void {
    const id = this.overrideTargetId();
    if (!id || !this.overrideReason.trim()) return;
    this.api.overrideAiRecommendation(id, { reason: this.overrideReason.trim() }).subscribe({
      next: () => {
        this.cancelOverride();
        this.load();
      }
    });
  }

  cancelOverride(): void {
    this.overrideTargetId.set(null);
    this.overrideReason = '';
  }

  summaryFor(row: InsuranceAiRecommendationRecord): string {
    try {
      const parsed = JSON.parse(row.explanationJson) as { summary?: string };
      return parsed.summary || this.t('insurance.ai.copilot.noExplanation');
    } catch {
      return this.t('insurance.ai.copilot.noExplanation');
    }
  }

  t(key: string): string { return this.localization.translate(key); }
}
